import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, extractError } from '@/lib/api.js';
import { PageLayout } from '@/components/layout/PageLayout.js';
import { Dica } from '@/components/Dica.js';
import { TEMAS, relogio } from '@shared/conselheiro.js';
import { AtaDaManha } from '@/components/conselheiro/AtaDaManha.js';
import { SalaDoMeet } from '@/components/conselheiro/SalaDoMeet.js';
import { DossieDaManha, enquadramentoDoDossie } from '@/components/conselheiro/DossieDaManha.js';
import type { TemaDoDossie } from '@shared/conselheiro.js';
import { PainelDaGravacao, useGravador, type EstadoDoGravador } from '@/components/conselheiro/Gravador.js';

/**
 * A manhã do conselho, ao vivo. O Principal conduz: o roteiro em blocos com
 * cronômetro, presença e consentimento, desvios com enquadramento rápido e
 * decisões capturadas no tema em discussão. Sócios e responsáveis acompanham
 * a mesma tela, sem os controles. O modo apresentação é a tela da TV da sala.
 */

type Finalidade = 'OPERACAO' | 'QUALIDADE' | 'FORMACAO';
type Bloco = {
  chave: string;
  tipo: 'ABERTURA' | 'NUMEROS' | 'DECISOES' | 'TEMA' | 'CONVERSA' | 'FECHAMENTO';
  titulo: string;
  minutos: number;
  tema_pauta_id?: string;
  gasto_s: number;
  aberto_em: string | null;
};
type Decisao = {
  id: string;
  descricao: string;
  prazo: string | null;
  status: string;
  atrasada: boolean;
  manha_id: string | null;
  tema_pauta_id?: string | null;
  responsavel: { nome: string } | null;
};
type Conducao = {
  quem: 'CONSELHEIRO' | 'SOCIO' | 'RESPONSAVEL';
  principal: boolean;
  agora: string;
  empresa: { id: string; nome: string };
  manha: {
    id: string;
    tipo: string;
    formato: string;
    local: string | null;
    status: string;
    inicio_previsto: string;
    inicio_real: string | null;
    fim_real: string | null;
    relato_empresa: string | null;
    meet_link: string | null;
  };
  /** Só para conselheiros: o que o dossiê de preparação trouxe para cada tema da pauta. */
  dossie_por_tema: Record<string, TemaDoDossie>;
  roteiro: Bloco[];
  bloco_atual: string | null;
  participantes: {
    id: string;
    nome: string;
    papel: string;
    presente: boolean;
    eu: boolean;
    consentimentos: Record<Finalidade, boolean>;
  }[];
  gravacao: {
    pode: boolean;
    faltam: string[];
    armazenamento: boolean;
    fatias: number;
    proxima_ordem: number;
    segundos: number;
    fora_da_gravacao: boolean;
    trechos_privados: { inicio_em: string; fim_em: string | null }[];
    lista: {
      id: string;
      ordem: number | null;
      inicio_em: string | null;
      fim_em: string | null;
      tamanho: number;
      tipo: string;
      /** APP (fatias de áudio gravadas pelo app) | MEET (vídeo copiado do Meet). */
      origem: string;
    }[];
  };
  temas: { id: string; titulo: string; contexto: string | null; origem: string; tema: number | null; status: string }[];
  indicadores: {
    id: string;
    nome: string;
    unidade: string | null;
    faixa_min: number | null;
    faixa_max: number | null;
    valores: { competencia: string; valor: number; fora_da_faixa: boolean }[];
  }[];
  decisoes: Decisao[];
  responsaveis_possiveis: { membro_id: string; nome: string }[];
};

const FINALIDADE: Record<Finalidade, { nome: string; dica: string }> = {
  OPERACAO: { nome: 'Gravar para a ata', dica: 'Necessário para gravar a manhã: a gravação vira transcrição e ata.' },
  QUALIDADE: { nome: 'Revisão de qualidade', dica: 'A Hack pode ouvir trechos para cuidar da qualidade do conselho.' },
  FORMACAO: {
    nome: 'Formação de conselheiros',
    dica: 'Trechos anônimos podem ser usados na formação de novos conselheiros.',
  },
};
const PAPEL: Record<string, string> = {
  CONSELHEIRO: 'conselheiro',
  SOCIO: 'sócio',
  COLABORADOR: 'responsável',
  CONVIDADO: 'convidado',
};
const STATUS: Record<string, string> = {
  ABERTA: 'Aberta',
  EM_DIA: 'Em dia',
  EM_RISCO: 'Em risco',
  BLOQUEADA: 'Bloqueada',
  CONCLUIDA: 'Concluída',
  CANCELADA: 'Cancelada',
};

const campo = 'rounded-element border border-ha-border2 bg-transparent px-2 py-1.5 text-sm';
const botao = 'rounded-element px-3 py-1.5 text-[10px] font-bold uppercase text-black disabled:opacity-50';
const msg = (e: unknown) => extractError(e).message;
const mes = (iso: string) =>
  new Date(iso).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric', timeZone: 'UTC' });

/** O relógio do servidor, corrido no navegador: o cronômetro não depende do relógio do computador. */
function useAgora(servidor: string | undefined) {
  const [agora, setAgora] = useState(() => Date.now());
  const desvio = useRef(0);
  useEffect(() => {
    if (servidor) desvio.current = Date.parse(servidor) - Date.now();
  }, [servidor]);
  useEffect(() => {
    const t = setInterval(() => setAgora(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  return agora + desvio.current;
}

function decorrido(b: Bloco, agora: number) {
  return b.gasto_s + (b.aberto_em ? Math.max(0, (agora - Date.parse(b.aberto_em)) / 1000) : 0);
}

function Caixa({ titulo, dica, children }: { titulo: string; dica?: string; children: ReactNode }) {
  return (
    <section className="rounded-card border border-ha-border2 p-4">
      <h3 className="mb-3 flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-ha-muted">
        {titulo}
        {dica ? <Dica texto={dica} /> : null}
      </h3>
      {children}
    </section>
  );
}

export function ConselheiroManhaPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const chave = ['conselheiro-conducao', id];
  const q = useQuery({
    queryKey: chave,
    queryFn: async () => (await api.get<Conducao>(`/conselheiro/manhas/${id}/conducao`)).data,
    // Quem acompanha (e a TV) vê o bloco mudar sem recarregar.
    refetchInterval: (query) => (query.state.data?.manha.status === 'EM_ANDAMENTO' ? 10_000 : false),
  });
  const [erro, setErro] = useState<string | null>(null);
  const escrever = useMutation({
    mutationFn: async (f: () => Promise<unknown>) => f(),
    onSuccess: () => {
      setErro(null);
      void qc.invalidateQueries({ queryKey: chave });
      void qc.invalidateQueries({ queryKey: ['conselheiro-mes'] });
    },
    onError: (e) => setErro(msg(e)),
  });
  const enviar = (f: () => Promise<unknown>) =>
    escrever.mutateAsync(f).then(
      () => true,
      () => false,
    );
  const agora = useAgora(q.data?.agora);
  const g = useGravador(id, q.data?.gravacao.proxima_ordem ?? 1, () => void qc.invalidateQueries({ queryKey: chave }));
  // Consentimento retirado ou alguém novo sem consentir: a gravação para sozinha.
  const semConsentimento = q.data ? !q.data.gravacao.pode : false;
  useEffect(() => {
    if (g.estado === 'gravando' && semConsentimento) {
      void g.parar();
      setErro('A gravação parou: falta o consentimento de algum presente.');
    }
  }, [g, semConsentimento]);

  const tela = useRef<HTMLDivElement>(null);
  const [apresentacao, setApresentacao] = useState(false);
  useEffect(() => {
    const sair = () => !document.fullscreenElement && setApresentacao(false);
    document.addEventListener('fullscreenchange', sair);
    return () => document.removeEventListener('fullscreenchange', sair);
  }, []);
  const apresentar = async () => {
    setApresentacao(true);
    await tela.current?.requestFullscreen?.().catch(() => undefined);
  };

  if (q.isLoading) return <PageLayout title="Manhã do conselho">Carregando…</PageLayout>;
  if (q.isError || !q.data) {
    return (
      <PageLayout title="Manhã do conselho">
        <p className="text-sm text-red-600">{msg(q.error)}</p>
      </PageLayout>
    );
  }
  const c = q.data;
  const atual = c.roteiro.find((b) => b.chave === c.bloco_atual) ?? null;
  const total = c.manha.inicio_real
    ? ((c.manha.fim_real ? Date.parse(c.manha.fim_real) : agora) - Date.parse(c.manha.inicio_real)) / 1000
    : 0;
  const previstoTotal = c.roteiro.reduce((s, b) => s + b.minutos * 60, 0);
  const emAndamento = c.manha.status === 'EM_ANDAMENTO';
  const conduz = c.principal && emAndamento;
  const indiceAtual = c.roteiro.findIndex((b) => b.chave === c.bloco_atual);
  const proximo = indiceAtual >= 0 ? c.roteiro[indiceAtual + 1] : undefined;
  const desvios = c.temas.filter((t) => t.origem === 'DESVIO');
  const daManha = c.decisoes.filter((d) => d.manha_id === c.manha.id);
  const voltar = c.quem === 'CONSELHEIRO' ? `/direcao/conselheiro/empresa/${c.empresa.id}` : '/direcao/conselheiro';

  const irPara = (chaveDoBloco: string) =>
    enviar(() => api.post(`/conselheiro/manhas/${id}/bloco`, { chave: chaveDoBloco }));

  // Um só contêiner nos dois modos: é ele que fica em tela cheia, e trocá-lo
  // de lugar no meio faria o navegador sair dela.
  return (
    <div
      ref={tela}
      className={apresentacao ? 'flex min-h-screen flex-col gap-8 overflow-auto bg-ha-bg p-10 text-ha-text' : ''}
    >
      {apresentacao ? (
        <>
          <ModoApresentacao c={c} atual={atual} agora={agora} total={total} daManha={daManha} gravando={g.estado} />
          <button
            type="button"
            className="self-start text-xs text-ha-muted underline"
            onClick={() => (document.fullscreenElement ? document.exitFullscreen() : setApresentacao(false))}
          >
            Sair do modo apresentação (Esc)
          </button>
        </>
      ) : (
        <PageLayout
          wide
          title={`Manhã do conselho · ${c.empresa.nome}`}
          description={
            c.manha.status === 'REALIZADA'
              ? `Realizada em ${new Date(c.manha.inicio_real ?? c.manha.inicio_previsto).toLocaleDateString('pt-BR')} · durou ${relogio(total)}`
              : emAndamento
                ? `Em andamento · ${relogio(total)} de ${relogio(previstoTotal)} previstos`
                : 'Ainda não começou.'
          }
          actions={
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <Link to={voltar} className="text-accent underline">
                ← Conselheiro
              </Link>
              {emAndamento ? (
                <button type="button" className="text-accent underline" onClick={apresentar}>
                  Modo apresentação
                </button>
              ) : null}
            </div>
          }
        >
          {erro ? <p className="mb-4 text-sm text-red-600">{erro}</p> : null}
          {c.manha.formato === 'ONLINE' && !['REALIZADA', 'CANCELADA'].includes(c.manha.status) ? (
            <div className="mb-4">
              <Caixa
                titulo="Sala do Meet"
                dica="A manhã online acontece no Google Meet. A sala é criada na conta Google que o conselheiro conectou no HackOS."
              >
                <SalaDoMeet
                  manhaId={id}
                  link={c.manha.meet_link}
                  principal={c.principal}
                  aoCriar={() => void qc.invalidateQueries({ queryKey: chave })}
                />
              </Caixa>
            </div>
          ) : null}
          {!c.manha.inicio_real ? (
            <div className="flex flex-col gap-4">
              <p className="text-sm text-ha-subtle">O Conselheiro Principal inicia a manhã pela tela do mês.</p>
              {c.quem === 'CONSELHEIRO' && c.manha.status !== 'CANCELADA' ? <DossieDaManha manhaId={id} /> : null}
            </div>
          ) : (
            <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
              <aside className="flex flex-col gap-4">
                <Caixa
                  titulo="Roteiro"
                  dica="A pauta confirmada vira blocos com tempo previsto. O cronômetro conta o tempo de cada bloco."
                >
                  <ol className="flex flex-col gap-1">
                    {c.roteiro.map((b) => {
                      const d = decorrido(b, agora);
                      const estourou = d > b.minutos * 60;
                      const ativo = b.chave === c.bloco_atual;
                      return (
                        <li key={b.chave}>
                          <button
                            type="button"
                            disabled={!conduz || ativo}
                            onClick={() => irPara(b.chave)}
                            className={`flex w-full items-baseline justify-between gap-2 rounded-element px-2 py-1.5 text-left text-sm ${
                              ativo ? 'bg-ha-card2 font-semibold text-ha-text' : 'text-ha-subtle'
                            } ${conduz && !ativo ? 'hover:bg-ha-card2' : ''}`}
                          >
                            <span className="min-w-0 break-words">{b.titulo}</span>
                            <span
                              className={`shrink-0 text-xs tabular-nums ${estourou ? 'text-red-500' : 'text-ha-muted'}`}
                            >
                              {d > 0 ? relogio(d) : `${b.minutos} min`}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ol>
                </Caixa>
                {desvios.length ? (
                  <Caixa titulo="Surgiu na manhã">
                    <ul className="flex flex-col gap-1 text-sm">
                      {desvios.map((t) => (
                        <li key={t.id}>
                          {t.titulo}
                          {t.tema != null ? <span className="text-xs text-ha-muted"> · {TEMAS[t.tema]}</span> : null}
                        </li>
                      ))}
                    </ul>
                  </Caixa>
                ) : null}
              </aside>

              <div className="flex min-w-0 flex-col gap-4">
                {c.manha.status === 'REALIZADA' ? (
                  <AtaDaManha manhaId={id} empresaId={c.empresa.id} responsaveis={c.responsaveis_possiveis} />
                ) : null}
                {conduz && c.manha.meet_link ? (
                  <Caixa
                    titulo="Gravação no Meet"
                    dica="Na manhã online, quem grava e transcreve é o Meet. Depois de encerrada, a gravação e a transcrição vêm para cá sozinhas."
                  >
                    <GravacaoNoMeet gravacao={c.gravacao} />
                  </Caixa>
                ) : conduz ? (
                  <Caixa
                    titulo="Gravação"
                    dica="O áudio vai em fatias de 5 minutos para o armazenamento privado, enquanto a manhã segue. Fora da gravação, nada é gravado."
                  >
                    <PainelDaGravacao g={g} gravacao={c.gravacao} agora={agora} />
                  </Caixa>
                ) : emAndamento && c.manha.meet_link ? (
                  <p className="text-xs text-ha-muted">
                    Se esta manhã for gravada, quem grava é o próprio Meet, que avisa a todos na reunião — e só com o
                    consentimento de todos os presentes.
                  </p>
                ) : emAndamento ? (
                  <p className="text-xs text-ha-muted">
                    {c.gravacao.fora_da_gravacao
                      ? 'Fora da gravação agora: nada está sendo gravado.'
                      : c.gravacao.fatias
                        ? `Esta manhã está sendo gravada para a ata · ${relogio(c.gravacao.segundos)} até aqui.`
                        : 'Esta manhã ainda não está sendo gravada.'}
                  </p>
                ) : null}
                {atual ? (
                  <div className="flex flex-wrap items-baseline justify-between gap-3 rounded-card border border-ha-border2 p-4">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-ha-muted">Agora</p>
                      <p className="text-lg font-semibold text-ha-text">{atual.titulo}</p>
                    </div>
                    <div className="flex items-baseline gap-4">
                      <span
                        className={`text-3xl font-semibold tabular-nums ${decorrido(atual, agora) > atual.minutos * 60 ? 'text-red-500' : 'text-ha-text'}`}
                      >
                        {relogio(decorrido(atual, agora))}
                      </span>
                      <span className="text-sm text-ha-muted">de {atual.minutos} min</span>
                      {conduz && proximo ? (
                        <button
                          type="button"
                          className={botao}
                          style={{ background: 'var(--primary)' }}
                          onClick={() => irPara(proximo.chave)}
                        >
                          Próximo bloco
                        </button>
                      ) : null}
                    </div>
                  </div>
                ) : null}

                {atual?.tipo === 'ABERTURA' || !emAndamento ? <Presenca c={c} conduz={conduz} enviar={enviar} /> : null}
                {atual?.tipo === 'NUMEROS' ? <Numeros c={c} /> : null}
                {atual?.tipo === 'DECISOES' ? (
                  <DecisoesAbertas decisoes={c.decisoes.filter((d) => d.manha_id !== c.manha.id)} />
                ) : null}
                {atual?.tipo === 'TEMA' ? <TemaEmDiscussao c={c} bloco={atual} /> : null}
                {atual && ['NUMEROS', 'TEMA', 'CONVERSA'].includes(atual.tipo) && conduz ? (
                  <Desvio manhaId={id} enviar={enviar} />
                ) : null}
                {atual &&
                ['TEMA', 'CONVERSA', 'FECHAMENTO'].includes(atual.tipo) &&
                (conduz || (emAndamento && c.quem === 'SOCIO')) ? (
                  <CapturarDecisao c={c} temaPautaId={atual.tema_pauta_id ?? null} enviar={enviar} />
                ) : null}
                {atual?.tipo === 'FECHAMENTO' || !emAndamento ? <DecisoesDaManha decisoes={daManha} /> : null}
                {!emAndamento && c.principal && c.gravacao.lista.length ? (
                  <FatiasDaGravacao gravacao={c.gravacao} />
                ) : null}
                {atual?.tipo === 'FECHAMENTO' && conduz ? (
                  <Caixa titulo="Encerrar">
                    <p className="mb-3 text-sm text-ha-subtle">
                      Ao encerrar, os temas da pauta viram discutidos e os sócios recebem o pedido de avaliação da
                      manhã.
                    </p>
                    <button
                      type="button"
                      className={botao}
                      style={{ background: 'var(--primary)' }}
                      onClick={async () => {
                        if (!window.confirm('Encerrar a manhã?')) return;
                        // A última fatia sobe antes de a manhã fechar.
                        if (g.estado !== 'parado' || g.pendentes) await g.parar();
                        await enviar(() => api.post(`/conselheiro/manhas/${id}/encerrar`));
                      }}
                    >
                      Encerrar a manhã
                    </button>
                  </Caixa>
                ) : null}
              </div>
            </div>
          )}
        </PageLayout>
      )}
    </div>
  );
}

type Enviar = (f: () => Promise<unknown>) => Promise<boolean>;

function Presenca({ c, conduz, enviar }: { c: Conducao; conduz: boolean; enviar: Enviar }) {
  const [convidado, setConvidado] = useState('');
  return (
    <Caixa
      titulo="Presença e consentimento"
      dica="Pergunte na abertura. Gravar a manhã exige o consentimento de todos os presentes para a ata; os outros dois são opcionais."
    >
      <ul className="flex flex-col divide-y divide-ha-border2">
        {c.participantes.map((p) => {
          const podeConsentir = conduz || (p.eu && c.manha.status === 'EM_ANDAMENTO');
          return (
            <li key={p.id} className="flex flex-wrap items-center justify-between gap-3 py-2 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={p.presente}
                  disabled={!conduz}
                  onChange={(e) => {
                    // Lido agora: quando o envio roda, o React já devolveu a caixa ao estado anterior.
                    const presente = e.target.checked;
                    void enviar(() => api.patch(`/conselheiro/participantes/${p.id}`, { presente }));
                  }}
                />
                <span className={p.presente ? 'text-ha-text' : 'text-ha-muted line-through'}>{p.nome}</span>
                <span className="text-xs text-ha-muted">{PAPEL[p.papel] ?? p.papel}</span>
              </label>
              <span className="flex flex-wrap gap-3">
                {(Object.keys(FINALIDADE) as Finalidade[]).map((f) => (
                  <label key={f} className="flex items-center gap-1 text-xs text-ha-subtle" title={FINALIDADE[f].dica}>
                    <input
                      type="checkbox"
                      checked={p.consentimentos[f]}
                      disabled={!podeConsentir}
                      onChange={(e) => {
                        const aceito = e.target.checked;
                        void enviar(() =>
                          api.put(`/conselheiro/participantes/${p.id}/consentimentos`, { [f]: aceito }),
                        );
                      }}
                    />
                    {FINALIDADE[f].nome}
                  </label>
                ))}
              </span>
            </li>
          );
        })}
      </ul>
      <p className={`mt-3 text-xs ${c.gravacao.pode ? 'text-emerald-600' : 'text-ha-muted'}`}>
        {c.gravacao.pode
          ? 'Todos os presentes consentiram em gravar para a ata.'
          : c.gravacao.faltam.length
            ? `Para gravar, falta o consentimento de: ${c.gravacao.faltam.join(', ')}.`
            : 'Marque quem está presente.'}
      </p>
      {conduz ? (
        <form
          className="mt-3 flex flex-wrap gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void enviar(() => api.post(`/conselheiro/manhas/${c.manha.id}/participantes`, { nome: convidado })).then(
              (ok) => ok && setConvidado(''),
            );
          }}
        >
          <input
            className={campo}
            placeholder="Nome de um convidado"
            value={convidado}
            onChange={(e) => setConvidado(e.target.value)}
          />
          <button
            type="submit"
            className={botao}
            style={{ background: 'var(--primary)' }}
            disabled={convidado.trim().length < 2}
          >
            Incluir
          </button>
        </form>
      ) : null}
    </Caixa>
  );
}

function Numeros({ c }: { c: Conducao }) {
  return (
    <Caixa titulo="Números do mês" dica="O último valor de cada indicador. Fora da faixa esperada aparece em vermelho.">
      {c.indicadores.length === 0 ? (
        <p className="text-sm text-ha-subtle">Nenhum indicador no plano do ciclo.</p>
      ) : (
        <ul className="flex flex-col gap-2 text-sm">
          {c.indicadores.map((i) => {
            const v = i.valores[0];
            return (
              <li key={i.id} className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-ha-text">{i.nome}</span>
                <span className={v?.fora_da_faixa ? 'font-semibold text-red-500' : 'text-ha-subtle'}>
                  {v
                    ? `${v.valor.toLocaleString('pt-BR')}${i.unidade ? ` ${i.unidade}` : ''} em ${mes(v.competencia)}`
                    : 'sem valor'}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {c.manha.relato_empresa ? (
        <p className="mt-4 whitespace-pre-wrap rounded-card border border-ha-border2 p-3 text-sm text-ha-subtle">
          {c.manha.relato_empresa}
        </p>
      ) : null}
    </Caixa>
  );
}

function DecisoesAbertas({ decisoes }: { decisoes: Decisao[] }) {
  const abertas = decisoes.filter((d) => !['CONCLUIDA', 'CANCELADA'].includes(d.status));
  return (
    <Caixa titulo="Andamento das decisões" dica="As decisões de manhãs anteriores que ainda não foram concluídas.">
      {abertas.length === 0 ? (
        <p className="text-sm text-ha-subtle">Nenhuma decisão em aberto.</p>
      ) : (
        <ul className="flex flex-col gap-2 text-sm">
          {abertas.map((d) => (
            <li key={d.id} className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="text-ha-text">{d.descricao}</span>
              <span className={`text-xs ${d.atrasada ? 'font-semibold text-red-500' : 'text-ha-muted'}`}>
                {d.responsavel?.nome ?? 'sem responsável'} · {d.atrasada ? 'atrasada' : (STATUS[d.status] ?? d.status)}
                {d.prazo ? ` · ${new Date(d.prazo).toLocaleDateString('pt-BR')}` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Caixa>
  );
}

/** No tema em discussão, só para conselheiros: as perguntas e hipóteses que o dossiê preparou. */
function DoDossie({ d }: { d: TemaDoDossie }) {
  const [aberto, setAberto] = useState(true);
  return (
    <div className="mt-3 rounded-element border border-dashed border-ha-border2 p-3 text-sm">
      <button type="button" className="text-xs font-semibold uppercase tracking-wide text-ha-muted" onClick={() => setAberto(!aberto)}>
        Do dossiê (só conselheiros) {aberto ? '▾' : '▸'}
      </button>
      {aberto ? (
        <div className="mt-2 flex flex-col gap-2">
          {enquadramentoDoDossie(d.enquadramento.tema) ? (
            <p className="text-xs text-ha-muted">Enquadramento: {enquadramentoDoDossie(d.enquadramento.tema)}</p>
          ) : null}
          {d.perguntas.length ? (
            <ul className="flex list-disc flex-col gap-1 pl-5 text-ha-text">
              {d.perguntas.map((x, i) => (
                <li key={i}>{x}</li>
              ))}
            </ul>
          ) : null}
          {d.hipoteses.length ? (
            <p className="text-xs text-ha-subtle">Hipóteses: {d.hipoteses.join(' · ')}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function TemaEmDiscussao({ c, bloco }: { c: Conducao; bloco: Bloco }) {
  const t = c.temas.find((x) => x.id === bloco.tema_pauta_id);
  const decisoes = c.decisoes.filter((d) => d.tema_pauta_id === bloco.tema_pauta_id);
  if (!t) return null;
  return (
    <Caixa titulo="Tema em discussão">
      <p className="text-base font-semibold text-ha-text">{t.titulo}</p>
      <p className="text-xs text-ha-muted">
        proposto por {t.origem === 'EMPRESA' ? 'a empresa' : 'o conselheiro'}
        {t.tema != null ? ` · ${TEMAS[t.tema]}` : ''}
      </p>
      {t.contexto ? <p className="mt-2 whitespace-pre-wrap text-sm text-ha-subtle">{t.contexto}</p> : null}
      {c.dossie_por_tema[t.id] ? <DoDossie d={c.dossie_por_tema[t.id]!} /> : null}
      {decisoes.length ? (
        <ul className="mt-3 flex flex-col gap-1 text-sm">
          {decisoes.map((d) => (
            <li key={d.id}>✓ {d.descricao}</li>
          ))}
        </ul>
      ) : null}
    </Caixa>
  );
}

function Desvio({ manhaId, enviar }: { manhaId: string; enviar: Enviar }) {
  const [f, setF] = useState({ titulo: '', tema: '' });
  return (
    <Caixa
      titulo="Registrar um desvio"
      dica="O que surgiu na conversa e não estava na pauta. Classifique pela força e tema do método: vira tema da manhã."
    >
      <form
        className="flex flex-wrap gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void enviar(() =>
            api.post(`/conselheiro/manhas/${manhaId}/desvios`, {
              titulo: f.titulo,
              tema: f.tema === '' ? null : Number(f.tema),
            }),
          ).then((ok) => ok && setF({ titulo: '', tema: '' }));
        }}
      >
        <input
          className={`${campo} min-w-0 flex-1`}
          placeholder="Ex.: atraso nas entregas do maior cliente"
          value={f.titulo}
          onChange={(e) => setF({ ...f, titulo: e.target.value })}
        />
        <select className={campo} value={f.tema} onChange={(e) => setF({ ...f, tema: e.target.value })}>
          <option value="">Tema do método</option>
          {TEMAS.map((t, i) => (
            <option key={t} value={i}>
              {t}
            </option>
          ))}
        </select>
        <button
          type="submit"
          className={botao}
          style={{ background: 'var(--primary)' }}
          disabled={f.titulo.trim().length < 3}
        >
          Registrar
        </button>
      </form>
    </Caixa>
  );
}

function CapturarDecisao({ c, temaPautaId, enviar }: { c: Conducao; temaPautaId: string | null; enviar: Enviar }) {
  const vazio = { descricao: '', responsavel: '', prazo: '', sucesso: '' };
  const [f, setF] = useState(vazio);
  return (
    <Caixa titulo="Capturar decisão" dica="O que ficou decidido, quem executa, até quando e como saber que deu certo.">
      <form
        className="grid gap-2 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          void enviar(() =>
            api.post(`/conselheiro/empresas/${c.empresa.id}/decisoes`, {
              descricao: f.descricao,
              responsavel_membro_id: f.responsavel || null,
              prazo: f.prazo || null,
              indicador_sucesso: f.sucesso || null,
              manha_id: c.manha.id,
              tema_pauta_id: temaPautaId,
              tema: c.temas.find((t) => t.id === temaPautaId)?.tema ?? null,
            }),
          ).then((ok) => ok && setF(vazio));
        }}
      >
        <input
          className={`${campo} sm:col-span-2`}
          placeholder="O que foi decidido"
          value={f.descricao}
          onChange={(e) => setF({ ...f, descricao: e.target.value })}
        />
        <select className={campo} value={f.responsavel} onChange={(e) => setF({ ...f, responsavel: e.target.value })}>
          <option value="">Responsável</option>
          {c.responsaveis_possiveis.map((r) => (
            <option key={r.membro_id} value={r.membro_id}>
              {r.nome}
            </option>
          ))}
        </select>
        <input className={campo} type="date" value={f.prazo} onChange={(e) => setF({ ...f, prazo: e.target.value })} />
        <input
          className={`${campo} sm:col-span-2`}
          placeholder="Sinal de que deu certo (opcional)"
          value={f.sucesso}
          onChange={(e) => setF({ ...f, sucesso: e.target.value })}
        />
        <div>
          <button
            type="submit"
            className={botao}
            style={{ background: 'var(--primary)' }}
            disabled={f.descricao.trim().length < 5}
          >
            Registrar decisão
          </button>
        </div>
      </form>
    </Caixa>
  );
}

function DecisoesDaManha({ decisoes }: { decisoes: Decisao[] }) {
  return (
    <Caixa titulo="Decisões desta manhã">
      {decisoes.length === 0 ? (
        <p className="text-sm text-ha-subtle">Nenhuma decisão registrada nesta manhã.</p>
      ) : (
        <ul className="flex flex-col gap-2 text-sm">
          {decisoes.map((d) => (
            <li key={d.id} className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="text-ha-text">{d.descricao}</span>
              <span className="text-xs text-ha-muted">
                {d.responsavel?.nome ?? 'sem responsável'}
                {d.prazo ? ` · até ${new Date(d.prazo).toLocaleDateString('pt-BR')}` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Caixa>
  );
}

/** A tela da TV: o bloco atual em letra grande, o roteiro e o que já foi decidido. Sem controles. */
function ModoApresentacao({
  c,
  atual,
  agora,
  total,
  daManha,
  gravando,
}: {
  c: Conducao;
  atual: Bloco | null;
  agora: number;
  total: number;
  daManha: Decisao[];
  gravando: EstadoDoGravador;
}) {
  const d = atual ? decorrido(atual, agora) : 0;
  return (
    <>
      <div className="flex items-baseline justify-between gap-6">
        <p className="text-xl text-ha-subtle">{c.empresa.nome} · manhã do conselho</p>
        <p className="flex items-center gap-4 text-xl tabular-nums text-ha-subtle">
          {gravando === 'gravando' ? (
            <span className="flex items-center gap-2 text-base">
              <span aria-hidden className="inline-block h-3 w-3 animate-pulse rounded-full bg-red-500" /> Gravando
            </span>
          ) : gravando === 'fora' ? (
            <span className="text-base">Fora da gravação</span>
          ) : null}
          {relogio(total)}
        </p>
      </div>
      {atual ? (
        <div>
          <p className="text-5xl font-semibold leading-tight">{atual.titulo}</p>
          <p className={`mt-4 text-7xl font-semibold tabular-nums ${d > atual.minutos * 60 ? 'text-red-500' : ''}`}>
            {relogio(d)} <span className="text-3xl text-ha-muted">/ {atual.minutos} min</span>
          </p>
        </div>
      ) : null}
      <div className="grid gap-10 md:grid-cols-2">
        <ol className="flex flex-col gap-2 text-2xl">
          {c.roteiro.map((b) => (
            <li key={b.chave} className={b.chave === atual?.chave ? 'font-semibold text-ha-text' : 'text-ha-muted'}>
              {b.titulo}
            </li>
          ))}
        </ol>
        <div>
          <p className="mb-3 text-2xl font-semibold">Decidido nesta manhã</p>
          {daManha.length ? (
            <ul className="flex flex-col gap-2 text-xl">
              {daManha.map((x) => (
                <li key={x.id}>
                  ✓ {x.descricao}
                  {x.responsavel ? <span className="text-ha-muted"> · {x.responsavel.nome}</span> : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xl text-ha-muted">Nada ainda.</p>
          )}
        </div>
      </div>
    </>
  );
}

/**
 * Manhã online: o Meet grava. O app só diz se pode — a mesma regra da gravação
 * pelo app, o consentimento de todos os presentes — e como fazer.
 */
function GravacaoNoMeet({ gravacao }: { gravacao: Conducao['gravacao'] }) {
  return (
    <div className="flex flex-col gap-2 text-sm">
      {gravacao.pode ? (
        <p className="font-semibold text-ha-text">
          Todos os presentes consentiram: pode gravar. No Meet, em <em>Atividades › Gravação</em>, ligue a gravação
          marcando <em>Também transcrever</em>.
        </p>
      ) : (
        <p className="font-semibold text-red-600">
          Não grave ainda: falta o consentimento de{' '}
          {gravacao.faltam.length ? gravacao.faltam.join(', ') : 'alguém — marque quem está presente'}.
        </p>
      )}
      <p className="text-ha-subtle">
        Para um assunto fora da gravação, pare a gravação no Meet e ligue de novo depois. Se alguém retirar o
        consentimento, pare na hora: sem o consentimento de todos, nada do Meet vem para o app.
      </p>
      <p className="text-xs text-ha-muted">
        Depois de encerrar, a gravação e a transcrição (com quem falou) vêm do Meet para cá, em minutos ou poucas horas;
        os originais vão para a lixeira do seu Drive.
      </p>
    </div>
  );
}

/** Depois da manhã, o Principal ouve as fatias para conferir a ata. */
function FatiasDaGravacao({ gravacao }: { gravacao: Conducao['gravacao'] }) {
  const [erro, setErro] = useState<string | null>(null);
  const ouvir = async (id: string) => {
    setErro(null);
    const aba = window.open('', '_blank');
    try {
      const { data } = await api.get<{ url: string }>(`/conselheiro/anexos/${id}/link`, { params: { modo: 'ver' } });
      if (aba) aba.location.href = data.url;
    } catch (e) {
      aba?.close();
      setErro(msg(e));
    }
  };
  const hora = (iso: string | null) =>
    iso
      ? new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' })
      : '—';
  return (
    <Caixa titulo="Gravação" dica="Só o Conselheiro Principal ouve a gravação: ela existe para a ata.">
      <p className="mb-2 text-sm text-ha-subtle">
        {relogio(gravacao.segundos)} gravados em {gravacao.lista.length}{' '}
        {gravacao.lista.every((f) => f.origem === 'MEET')
          ? gravacao.lista.length === 1
            ? 'vídeo do Meet'
            : 'vídeos do Meet'
          : gravacao.lista.length === 1
            ? 'fatia'
            : 'fatias'}
        {gravacao.trechos_privados.length
          ? ` · ${gravacao.trechos_privados.length} ${gravacao.trechos_privados.length === 1 ? 'trecho' : 'trechos'} fora da gravação`
          : ''}
        .
      </p>
      <ul className="flex flex-wrap gap-2 text-xs">
        {gravacao.lista.map((f) => (
          <li key={f.id}>
            <button type="button" className="text-accent underline" onClick={() => ouvir(f.id)}>
              {hora(f.inicio_em)}–{hora(f.fim_em)}
            </button>
          </li>
        ))}
      </ul>
      {erro ? <p className="mt-2 text-xs text-red-600">{erro}</p> : null}
    </Caixa>
  );
}
